import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import { ErrorApi } from '../api/errores'

/**
 * Trazabilidad de rutas: el "parte de reparto" que el subworkflow n8n calcula y
 * ENVÍA al repartidor cada día a las 5:00. Dos consumidores:
 *   - el calendario (parte del día, con el detalle de grupos/paradas)
 *   - Gestión › Partes de reparto (histórico resumido)
 *
 * Tolera que el backend aún no tenga partes (tabla vacía / 4xx) → se muestra
 * "sin ruta enviada" en vez de romper la pantalla.
 */

/** Una parada dentro de un grupo, en orden de visita. */
export interface ParadaReparto {
  orden: number
  contactId: string | null
  nombre: string
  direccion: string
  telefono: string | null
  /** Indicación de entrega del cliente ("traer antes de las 8:30"); null si no hay. */
  notaEntrega: string | null
}

/** Un grupo/cluster de la ruta con su link de Google Maps. */
export interface GrupoReparto {
  etiqueta: string
  color: string | null
  mapsUrl: string | null
  paradas: ParadaReparto[]
}

/** Parte completo de un día (lo que ve el calendario). */
export interface ParteReparto {
  fecha: string
  grupos: GrupoReparto[]
  totalParadas: number
  totalGrupos: number
  totalNotas: number
  destinatario: string | null
  enviadoEn: string
}

/** Resumen de un día para el histórico de Gestión (sin detalle de paradas). */
export interface ResumenParte {
  fecha: string
  totalParadas: number
  totalGrupos: number
  totalNotas: number
  destinatario: string | null
  enviadoEn: string
}

const RUTA = '/api/partes-reparto'

/** Histórico de partes para Gestión › Partes de reparto. */
export function usePartesReparto(limite = 60) {
  const api = useApi()
  return useQuery<ResumenParte[], ErrorApi>({
    queryKey: ['partes-reparto', limite],
    queryFn: async ({ signal }) => {
      try {
        const datos = await api.pedir<ResumenParte[]>(RUTA, {
          parametros: { limite: String(limite) },
          senal: signal,
        })
        return datos ?? []
      } catch (error) {
        if (error instanceof ErrorApi && error.estado < 500) return []
        throw error
      }
    },
    retry: (intentos, error) => {
      if (error instanceof ErrorApi && error.estado < 500) return false
      return intentos < 2
    },
  })
}

/**
 * Parte de un día concreto (calendario). Devuelve null si ese día no se envió
 * ruta — el popover muestra entonces el estado "sin ruta".
 */
export function useParteDelDia(fecha: string | null) {
  const api = useApi()
  return useQuery<ParteReparto | null, ErrorApi>({
    queryKey: ['parte-reparto', fecha],
    enabled: !!fecha,
    queryFn: async ({ signal }) => {
      try {
        const datos = await api.pedir<ParteReparto | null>(`${RUTA}/${fecha}`, { senal: signal })
        return datos ?? null
      } catch (error) {
        if (error instanceof ErrorApi && error.estado < 500) return null
        throw error
      }
    },
    retry: (intentos, error) => {
      if (error instanceof ErrorApi && error.estado < 500) return false
      return intentos < 2
    },
  })
}
